import { DateTime } from 'luxon';

// Aegis Track A campaign update — wire exactly:
//   PATCH /v1/campaigns/{id}
//   Auth: Cognito + LEGAL_WALLETS (same as POST /campaigns)
//   Allocated campaigns only for Cap / expiry edits.
//   body (at least one field; omit = leave unchanged):
//     { solLimit?: number, expireAt?: ISO-8601 }
//   200: updated campaign (capMode, solLimit, expireAt, allocatedSol,
//        allocatedCount). Optional helpers: softFreezeAt, mutable,
//        mutableReason. Absent helpers → frozen when now > expireAt - 2h.
//   400 CAMPAIGN_SOFT_FROZEN / SOL_LIMIT_BELOW_ALLOCATED /
//       EXPIRE_AT_IN_PAST / EXPIRE_AT_SHORTEN_FORBIDDEN /
//       CAP_MODE_NOT_ALLOCATED
//   expireAt is extend-only (aegis#113 @ 0c3c4bfbdf837424ab13ef4abb74f66d41213a4e):
//   a PATCH that moves it earlier is HTTP 400 with code exactly
//   EXPIRE_AT_SHORTEN_FORBIDDEN. Cap raise + 2h freeze unchanged.
//   403 / 404 as usual
export const CAMPAIGN_EDIT_FREEZE_HOURS = 2;

// Confirmed aegis#113 @ 0c3c4bfbdf837424ab13ef4abb74f66d41213a4e (CI green).
export const AEGIS_EXPIRE_AT_SHORTEN_FORBIDDEN = 'EXPIRE_AT_SHORTEN_FORBIDDEN';

export const CAMPAIGN_UPDATE_CODES = {
  CAMPAIGN_SOFT_FROZEN:
    'Cap and expiry are frozen. Edits lock 2 hours before redeem-by so the last claims are not racing an admin change.',
  SOL_LIMIT_BELOW_ALLOCATED: 'Cap cannot be below the SOL already allocated to claims.',
  EXPIRE_AT_IN_PAST: 'Expiry cannot be in the past.',
  [AEGIS_EXPIRE_AT_SHORTEN_FORBIDDEN]:
    'Expiry can only be extended. The current redeem-by cannot be moved earlier.',
  CAP_MODE_NOT_ALLOCATED: 'Cap and expiry can only be edited on a claims-cap (allocated) campaign.',
};

export const parseExpireAt = expireAt => {
  if (!expireAt) return null;
  const dt = DateTime.fromISO(expireAt, { zone: 'utc' });
  return dt.isValid ? dt : null;
};

const sameInstantToMinute = (left, right) => {
  const a = parseExpireAt(left);
  const b = parseExpireAt(right);
  if (!a || !b) return false;
  return a.toUTC().startOf('minute').toISO() === b.toUTC().startOf('minute').toISO();
};

export const isCampaignEditFrozen = (expireAt, now = DateTime.utc()) => {
  const expiry = parseExpireAt(expireAt);
  if (!expiry) return false;
  return now > expiry.minus({ hours: CAMPAIGN_EDIT_FREEZE_HOURS });
};

// Prefer Aegis helpers when the 200 (or GET) carries them; otherwise derive.
export const campaignIsFrozen = (campaign, now = DateTime.utc()) => {
  if (!campaign) return false;
  if (campaign.mutable === false) return true;
  if (campaign.mutable === true) return false;
  if (campaign.softFreezeAt) {
    const at = parseExpireAt(campaign.softFreezeAt);
    return Boolean(at && now > at);
  }
  return isCampaignEditFrozen(campaign.expireAt, now);
};

export const campaignFrozenCopy = campaign =>
  (typeof campaign?.mutableReason === 'string' && campaign.mutableReason.trim()) ||
  CAMPAIGN_UPDATE_CODES.CAMPAIGN_SOFT_FROZEN;

export const campaignUpdateUnavailable = error => {
  const status = error?.response?.status;
  return status === 405 || status === 501;
};

export const campaignUpdateUnavailableCopy =
  'Aegis has no campaign update route yet — deploy PATCH /campaigns/{id} for solLimit + expireAt, then save again.';

export const campaignEditFrozenCopy = CAMPAIGN_UPDATE_CODES.CAMPAIGN_SOFT_FROZEN;

export const campaignUpdateCode = error => {
  const body = error?.response?.data;
  return body?.error?.code ?? body?.code ?? null;
};

export const campaignUpdateErrorCopy = (error, fallback) => {
  const code = campaignUpdateCode(error);
  if (code && CAMPAIGN_UPDATE_CODES[code]) return CAMPAIGN_UPDATE_CODES[code];
  const status = error?.response?.status;
  if (status === 403) {
    return (
      error?.response?.data?.error?.message ??
      error?.response?.data?.message ??
      'You cannot update this campaign.'
    );
  }
  if (status === 404) {
    return (
      error?.response?.data?.error?.message ??
      error?.response?.data?.message ??
      'That campaign was not found.'
    );
  }
  if (campaignUpdateUnavailable(error)) return campaignUpdateUnavailableCopy;
  return (
    error?.response?.data?.error?.message ??
    error?.response?.data?.message ??
    (typeof error?.response?.data === 'string' && error.response.data.trim()
      ? error.response.data
      : null) ??
    fallback ??
    'Could not update cap and expiry. Please try again.'
  );
};

export const validateCampaignEdit = ({
  solLimit,
  expireAt,
  allocatedSol,
  currentExpireAt,
  now = DateTime.utc(),
  changing = { solLimit: true, expireAt: true },
}) => {
  if (changing.solLimit) {
    if (!(Number(solLimit) > 0) || Number.isNaN(Number(solLimit))) {
      return 'A claims-cap campaign needs a SOL limit above 0.';
    }
    if (Number.isFinite(allocatedSol) && Number(solLimit) < allocatedSol) {
      return CAMPAIGN_UPDATE_CODES.SOL_LIMIT_BELOW_ALLOCATED;
    }
  }
  if (changing.expireAt) {
    const expiry = parseExpireAt(expireAt);
    if (!expiry) return 'Expiry must be a valid date.';
    if (now >= expiry) return CAMPAIGN_UPDATE_CODES.EXPIRE_AT_IN_PAST;
    const current = parseExpireAt(currentExpireAt);
    if (current && expiry.toUTC().startOf('minute') < current.toUTC().startOf('minute')) {
      return CAMPAIGN_UPDATE_CODES[AEGIS_EXPIRE_AT_SHORTEN_FORBIDDEN];
    }
    if (now > expiry.minus({ hours: CAMPAIGN_EDIT_FREEZE_HOURS })) {
      return CAMPAIGN_UPDATE_CODES.CAMPAIGN_SOFT_FROZEN;
    }
  }
  return null;
};

// Omit unchanged fields (PATCH: omit = leave unchanged). `previous` is the
// live campaign; without it every provided field is sent.
export const buildCampaignUpdateBody = ({ solLimit, expireAt } = {}, previous) => {
  const body = {};
  if (solLimit != null && !Number.isNaN(Number(solLimit))) {
    const next = Number(solLimit);
    if (!previous || Number(previous.solLimit) !== next) body.solLimit = next;
  }
  if (expireAt) {
    if (!previous || !sameInstantToMinute(previous.expireAt, expireAt)) body.expireAt = expireAt;
  }
  return body;
};
